import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { PolicyMediator } from '../../../http-services/mediators/policy.mediator';
import { QfaEventType } from '../../../shared/enums/event-type.enum';
import { PolicyNotFoundComponent } from '../../widgets/policy-not-found/policy-not-found.component';
import { WidgetsModule } from '../../widgets/widgets.module';

import { PolicySummaryService } from '../../../http-services/services/policy-summary.service';
import { PolicyPdf, PolicySearchSummary, PolicySummary } from '../../../interfaces/policy-summary.model';
import { AuthService } from '../../../shared/services/auth.service';
import { GlobalErrorHandler } from '../../../shared/services/error-handler.service';
import { LoggingService } from '../../../shared/services/logging.service';
import { PolicySummaryComponent } from '../../widgets/policy-summary/policy-summary.component';

@Component({
  selector: 'app-dashboard-screen',
  imports: [PolicyNotFoundComponent, WidgetsModule, ProgressSpinnerModule, PolicySummaryComponent],
  templateUrl: './dashboard-screen.component.html',
  styleUrl: './dashboard-screen.component.scss',
})
export class DashboardScreenComponent implements OnInit {
  protected readonly _route: ActivatedRoute = inject(ActivatedRoute);
  protected readonly _mediator: PolicyMediator = inject(PolicyMediator);
  protected readonly _oauthService = inject(AuthService);
  protected readonly _loggingService: LoggingService = inject(LoggingService);
  protected readonly _router: Router = inject(Router);
  protected readonly _policySummaryService: PolicySummaryService = inject(PolicySummaryService);
  protected readonly _errorHandlingService: GlobalErrorHandler = inject(GlobalErrorHandler);

  downloadQueue: string[] = [];
  loading: boolean = true;
  variantsNotFound: boolean = false;
  policyNotFound: boolean = false;
  private _policies?: PolicySearchSummary;

  ngOnInit() {
    if (this._oauthService.isLoggedIn()) {
      this._route.queryParams.subscribe(params => {
        const policyNumber = params['policyNumber'] ?? '';
        if (policyNumber) {
          this._fetchPolicies(policyNumber); // Fetch data using the policy number
        }
      });
    }
  }

  get policies(): PolicySummary[] {
    return this._policies?.policySummaries ?? [];
  }

  addNewQFA() {
    const policyNumber = this._route.snapshot.queryParamMap.get('policyNumber');

    if (!policyNumber) {
      return;
    }

    const urlTree = this._router.createUrlTree(['/coverage'], {
      queryParams: { policyNumber },
    });
    const url = this._router.serializeUrl(urlTree);

    window.open(url, '_self');
  }

  downloadQuote(policy: string) {
    this.downloadQueue.push(policy);
    this._policySummaryService
      .prepareVariantPdf(policy)
      .then((id: { letterId: string }) => {
        this._policySummaryService
          .getVariantPdf(id.letterId, policy)
          .then((response: PolicyPdf) => {
            this._downloadPDF(response);

            this.removeFromDownloadQueue(policy);
          })
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          .catch(error => {
            this.removeFromDownloadQueue(policy);
            this._showError(
              `${policy} Download Failure: An error has occurred, please call the Chubb Life team on 0508 464 999 for assistance.`,
            );
          });
      })
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .catch(error => {
        this.removeFromDownloadQueue(policy);
        this._showError(
          `${policy} Download Failure: An error has occurred, please call the Chubb Life team on 0508 464 999 for assistance.`,
        );
      });
  }

  private removeFromDownloadQueue(policy: string) {
    const newArray = structuredClone(this.downloadQueue);
    const index = newArray.findIndex(policyNo => policyNo === policy);
    if (index !== -1) {
      newArray.splice(index, 1);
      this.downloadQueue = newArray;
    }
  }

  private _downloadPDF(data: PolicyPdf) {
    if (data.base64DocumentPayload && data.name) {
      const linkSource = `data:application/pdf;base64,${data.base64DocumentPayload}`;
      const downloadLink = document.createElement('a');
      downloadLink.href = linkSource;
      downloadLink.download = data.name;
      downloadLink.click();
    }
  }

  private _showError(message: string) {
    this._errorHandlingService.setMessage(message);
    this._errorHandlingService.showError();
  }

  private _fetchPolicies(policyNumber: string): void {
    this._mediator
      .getPolicySummary(policyNumber)
      .then((response: PolicySearchSummary) => {
        this._loggingService.logEvent(
          QfaEventType.INFO,
          `FRONTEND.DashboardScreenComponent.fetchPolicy - policyNumber: ${policyNumber}`,
        );

        if (!response) {
          this._loggingService.logEvent(
            QfaEventType.WARNING,
            `FRONTEND.DashboardScreenComponent.fetchPolicy - error receiving data: ${response}`,
          );
        }
        this._policies = response;
      })
      .catch(error => {
        this.handleError(error, policyNumber); // Call an async method
      })
      .finally(() => {
        this.loading = false;
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async handleError(error: any, policyNumber: string): Promise<void> {
    if (error.status === 400 || error.status === 401) {
      // Token might be expired or invalid
      const loginSuccess = await this._oauthService.tryReauthenticate(
        'FRONTEND.DashboardScreenComponent.fetchPolicy - error: tryReauthenticate failed',
      );
      if (loginSuccess) {
        // Retry fetch after reauth
        setTimeout(() => this._fetchPolicies(policyNumber), 0);
      } else {
        this._router.navigate(['/access-denied']);
      }
    } else if (error.status === 404) {
      this.policyNotFound = true;
    } else {
      this._router.navigate(['/unknown-error']);
    }
  }
}
